import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2, Clock, Users, ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface PolicyAcknowledgementStatsProps {
  adminBubbleId: string;
}

export const PolicyAcknowledgementStats = ({
  adminBubbleId,
}: PolicyAcknowledgementStatsProps) => {
  const { t } = useLanguage();

  const { data: stats = [], isLoading } = useQuery({
    queryKey: ["policy-acknowledgement-stats", adminBubbleId],
    queryFn: async () => {
      // @ts-ignore - New RPC function not in types yet
      const { data, error } = await supabase.rpc("get_policy_acknowledgement_stats", {
        bubble_id: adminBubbleId,
      });

      if (error) throw error;
      return data || [];
    },
    enabled: !!adminBubbleId,
  });

  const getPolicyLabel = (policyType: string) => {
    switch (policyType) {
      case "code_of_ethics":
        return t("codeOfEthics");
      case "code_of_conduct":
        return t("codeOfConduct");
      case "gdpr_policy":
        return t("gdprPolicy");
      default:
        return policyType;
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="grid gap-6">
      {(stats as any[]).map((stat: any) => (
        <PolicyStatCard key={stat.policy_type} stat={stat} getPolicyLabel={getPolicyLabel} t={t} />
      ))}
    </div>
  );
};

interface PolicyStatCardProps {
  stat: any;
  getPolicyLabel: (type: string) => string;
  t: any;
}

const PolicyStatCard = ({ stat, getPolicyLabel, t }: PolicyStatCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: userDetails = [], isLoading: loadingUsers } = useQuery({
    queryKey: ["policy-user-details", stat.policy_id],
    queryFn: async () => {
      // @ts-ignore - New RPC function not in types yet
      const { data, error } = await supabase.rpc("get_policy_user_details", {
        p_policy_id: stat.policy_id,
      });

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen,
  });

  const acknowledgedUsers = (userDetails as any[]).filter((u: any) => u.acknowledged);
  const pendingUsers = (userDetails as any[]).filter((u: any) => !u.acknowledged);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {getPolicyLabel(stat.policy_type)}
            </CardTitle>
            <CardDescription>
              {t("acknowledgementRate")}: {stat.acknowledgement_rate}%
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>{t("acknowledgementRate")}</span>
            <span className="font-semibold">{stat.acknowledgement_rate}%</span>
          </div>
          <Progress value={stat.acknowledgement_rate} />
        </div>

        <div className="grid grid-cols-3 gap-4 pt-4 border-t">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{stat.total_users}</div>
            <div className="text-xs text-muted-foreground">{t("totalUsers")}</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
            <div className="text-2xl font-bold text-green-600">
              {stat.acknowledged_users}
            </div>
            <div className="text-xs text-muted-foreground">{t("acknowledgedUsers")}</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Clock className="h-4 w-4 text-orange-500" />
            </div>
            <div className="text-2xl font-bold text-orange-600">
              {stat.pending_users}
            </div>
            <div className="text-xs text-muted-foreground">{t("pendingUsers")}</div>
          </div>
        </div>

        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="pt-4 border-t">
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full">
              {isOpen ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show User Details
                </>
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4 space-y-4">
            {loadingUsers ? (
              <div className="text-center py-4 text-sm text-muted-foreground">Loading users...</div>
            ) : (
              <>
                {acknowledgedUsers.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      {t("acknowledgedUsers")} ({acknowledgedUsers.length})
                    </h4>
                    <ScrollArea className="h-[200px] rounded-md border p-3">
                      <div className="space-y-2">
                        {acknowledgedUsers.map((user: any) => (
                          <div
                            key={user.user_id}
                            className="flex items-center justify-between py-2 px-3 bg-green-50 dark:bg-green-950/20 rounded-md"
                          >
                            <div>
                              <p className="text-sm font-medium">{user.user_name}</p>
                              <p className="text-xs text-muted-foreground">{user.user_email}</p>
                            </div>
                            <Badge variant="outline" className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {new Date(user.acknowledged_at).toLocaleDateString()}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {pendingUsers.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-orange-600">
                      <Clock className="h-4 w-4" />
                      {t("pendingUsers")} ({pendingUsers.length})
                    </h4>
                    <ScrollArea className="h-[200px] rounded-md border p-3">
                      <div className="space-y-2">
                        {pendingUsers.map((user: any) => (
                          <div
                            key={user.user_id}
                            className="flex items-center justify-between py-2 px-3 bg-orange-50 dark:bg-orange-950/20 rounded-md"
                          >
                            <div>
                              <p className="text-sm font-medium">{user.user_name}</p>
                              <p className="text-xs text-muted-foreground">{user.user_email}</p>
                            </div>
                            <Badge variant="outline" className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};
